// ============================================
// SCHEDULE WIDGET - Widget de horarios para la landing
// ============================================

import {
  getSchedule,
  isOpenNow,
  getStatusMessage,
  formatScheduleDisplay,
} from "./scheduleService.js";

/**
 * Obtiene el HTML del widget de horarios
 */
export const getScheduleWidget = async () => {
  //console.log('[scheduleWidget] getScheduleWidget called');
  const schedule = await getSchedule();
  //console.log('[scheduleWidget] Schedule obtained:', schedule);
  
  // Si no hay schedule, no mostrar el widget
  if (!schedule) {
 //console.log('[scheduleWidget] No schedule found, hiding widget');
    return null;
  }
  
  // Verificar el campo "activo" del documento schedule
  // Si activo es false (explícitamente), no mostrar el widget
  // Si activo es true, undefined, o null, mostrar el widget
  /*console.log('[scheduleWidget] Checking activo field:', { 
    activo: schedule.activo, 
    activoType: typeof schedule.activo,
    activoStrictFalse: schedule.activo === false 
  });*/
  
  const isActive = schedule.activo !== false;
  //console.log('[scheduleWidget] isActive result:', isActive);
  
  if (!isActive) {
 //console.log('[scheduleWidget] Schedule is inactive (activo=false), hiding widget', { activo: schedule.activo });
    return null;
  }
  
  /*console.log('[scheduleWidget] Schedule is active, showing widget', { 
    activo: schedule.activo,
    bloques: schedule.bloques?.length || 0,
    schedule: schedule 
  });*/
  
  const isOpen = isOpenNow(schedule);
  const message = getStatusMessage(schedule);
  const display = formatScheduleDisplay(schedule);

  //console.log('[scheduleWidget] Widget data:', { isOpen, message, display });

  const statusClass = isOpen ? "schedule-widget--open" : "schedule-widget--closed";
  const statusIcon = isOpen ? "🟢" : "🔴";

  const widgetHtml = `
    <div class="schedule-widget ${statusClass}">
      <div class="schedule-widget__content">
        <div class="schedule-widget__status">
          <span class="schedule-widget__icon">${statusIcon}</span>
          <span class="schedule-widget__message">${message}</span>
        </div>
        <div class="schedule-widget__times">
          ${display}
        </div>
      </div>
    </div>
  `;
  
  //console.log('[scheduleWidget] Generated HTML:', widgetHtml);
  return widgetHtml;
};

// (la implementación más completa de createScheduleComponent
// se declara más abajo y realiza re-render al detectar cambios)

/**
 * Hook para verificar si está abierto ahora y ejecutar callback
 */
export const onScheduleChange = async (callback) => {
  let schedule = await getSchedule();
  let isOpen = isOpenNow(schedule);
  callback(isOpen, schedule);

  // Actualizar cada minuto (esperar al getSchedule y comparar correctamente)
  setInterval(async () => {
    try {
      const updatedSchedule = await getSchedule();
      const updatedIsOpen = isOpenNow(updatedSchedule);
      if (updatedIsOpen !== isOpen) {
        isOpen = updatedIsOpen;
        schedule = updatedSchedule;
        callback(updatedIsOpen, updatedSchedule);
      }
    } catch (err) {
      console.warn("Error polling schedule:", err);
    }
  }, 60000);
};

// Mejorar createScheduleComponent para re-renderizar cuando cambie el estado
const safeRender = async (target) => {
  try {
    const html = await getScheduleWidget();
    // Si la función devuelve contenido válido, renderizarlo.
    // Si retorna null/vacío/undefined, ocultar el widget
    if (html) {
      target.innerHTML = html;
      // Asegurar que el contenedor sea visible - remover cualquier display:none
      target.style.display = '';
      target.style.visibility = '';
      target.style.opacity = '';
   //console.log('[scheduleWidget] safeRender: rendered content into', target);
   //console.log('[scheduleWidget] safeRender: HTML content length', html.length);
   //console.log('[scheduleWidget] safeRender: target display style', target.style.display);
      return true;
    } else {
      // Si el horario está desactivado, ocultar el widget
      target.innerHTML = '';
      target.style.display = 'none';
   //console.log('[scheduleWidget] safeRender: schedule inactive, hiding widget');
      return false;
    }
  } catch (err) {
    console.warn("Error renderizando widget de horarios:", err);
    return false;
  }
};

// Re-definir createScheduleComponent para usar onScheduleChange y actualizar
export const createScheduleComponent = async (targetSelector) => {
  //console.log('[scheduleWidget] createScheduleComponent called with', targetSelector);
  const target = document.querySelector(targetSelector);
  if (!target) {
    console.warn('[scheduleWidget] createScheduleComponent: target not found for', targetSelector);
    return;
  }

  //console.log('[scheduleWidget] target found:', target);

  // Placeholder inmediato para asegurar que el contenedor no quede vacío
  // y ofrecer feedback al usuario mientras se carga el horario.
  target.innerHTML = `
    <div class="schedule-widget schedule-widget--loading" style="min-width:180px; display:block; background: #fff; color: #111; padding: 0.6rem; border-radius: 0.5rem; box-shadow: 0 4px 12px rgba(0,0,0,0.06);">
      <div class="schedule-widget__content">
        <div class="schedule-widget__status">⏳ <strong>WIDGET</strong> cargando horario…</div>
      </div>
    </div>
  `;

  //console.log('[scheduleWidget] placeholder set');

  // Intentar renderizar el contenido real; si falla, conservar el placeholder
  try {
    const ok = await safeRender(target);
 //console.log('[scheduleWidget] initial render ok:', ok);
  } catch (e) {
    console.warn("createScheduleComponent: fallo al renderizar inicialmente:", e);
  }

  // Escuchar cambios y re-renderizar cuando cambie el estado
  onScheduleChange(async () => {
    try {
      const ok = await safeRender(target);
   //console.log('[scheduleWidget] onScheduleChange re-render ok:', ok);
    } catch (err) {
      console.warn("Error re-renderizando widget de horarios:", err);
    }
  });
};

// Exponer globalmente para uso desde scripts inline en el head
if (typeof window !== 'undefined') {
  // La función se expondrá cuando el módulo se importe
  // Se puede acceder a través de window.createScheduleComponent después de la importación
  window.__scheduleWidgetModule = { createScheduleComponent };
}